import {
  Component,
  effect,
  inject,
  input,
  InputSignal,
  signal,
  WritableSignal,
} from '@angular/core';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  ETicketCategorySubMenuAction,
  ISortGroupWithTicketData,
  ITicketCategory,
} from '@web/dtos';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDragPreview,
  CdkDropList,
} from '@angular/cdk/drag-drop';
import { IDialogDataWithTicketCategory } from '../../dialogs/dialog';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { TicketCategoryMultiSelectDialogComponent } from '../../dialogs/ticket-category-multi-select-dialog/ticket-category-multi-select-dialog.component';
import { TicketCategorySubMenuComponent } from '../../feedback/ticket-category-sub-menu/ticket-category-sub-menu.component';
import { NgClass } from '@angular/common';
import { LabelTextInputComponent } from '../../data-input/form-inputs/label-text-input/label-text-input.component';
import {
  EDropListViewMode,
  TicketDragAndDropSupportService,
} from '../../../services/support/ticket-drag-and-drop-support/ticket-drag-and-drop-support.service';
import { CloseOnOffClick } from '../../../directives/close-on-off-click';
import { HoverHighlight } from '../../../directives/hover-highlight';
import { CmsTicketCategoryService } from '../../../services/cms-ticket-category/cms-ticket-category.service';

export enum ESwitchDirection {
  Backwards = -1,
  Forwards = 1,
}

interface ITicketCategoryForm {
  name: FormControl<string>;
  position: FormControl<number>;
}

@Component({
  selector: 'aw-ticket-category-drag-and-drop-list',
  imports: [
    ReactiveFormsModule,
    HoverHighlight,
    TicketCategorySubMenuComponent,
    NgClass,
    LabelTextInputComponent,
    CdkDragHandle,
    CdkDrag,
    CdkDragPreview,
    CdkDropList,
    CdkDragPlaceholder,
    HoverHighlight,
    HoverHighlight,
    CloseOnOffClick,
    HoverHighlight,
  ],
  templateUrl: './ticket-category-drag-and-drop-list.component.html',
  styles: ``,
})
export class TicketCategoryDragAndDropListComponent {
  public readonly ticketCategory: InputSignal<ITicketCategory> =
    input.required();
  public fb: FormBuilder = inject(FormBuilder);
  public readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  protected readonly cmsTicketCategoryService: CmsTicketCategoryService =
    inject(CmsTicketCategoryService);
  public dialogService: DialogService = inject(DialogService);
  public readonly ticketDragAndDropSupportService: TicketDragAndDropSupportService =
    inject(TicketDragAndDropSupportService);
  protected readonly isSubMenuVisible: WritableSignal<boolean> = signal(false);
  protected readonly isEditing: WritableSignal<boolean> = signal(false);
  public ticketCategoryForm: FormGroup<ITicketCategoryForm> = this.fb.group({
    name: this.fb.nonNullable.control<string>(''),
    position: this.fb.nonNullable.control<number>(0),
  });
  public isBeingDragged: WritableSignal<boolean> = signal(false);
  protected readonly highlightedTicket: WritableSignal<number | undefined> =
    signal(undefined);
  public isCategoryHeaderHovered: WritableSignal<boolean> = signal(false);
  public ticketsInCategory: ISortGroupWithTicketData[] = [];
  protected readonly ESwitchDirection: typeof ESwitchDirection =
    ESwitchDirection;
  protected readonly EDropListViewMode: typeof EDropListViewMode =
    EDropListViewMode;

  constructor() {
    effect((): void => {
      const ticketCategory: ITicketCategory = this.ticketCategory();
      this.ticketCategoryForm.patchValue({
        name: ticketCategory.name,
        position: ticketCategory.position,
      });
    });
    effect((): void => {
      const ticketCategoryId: string | undefined = this.ticketCategory().id;
      this.ticketsInCategory = ticketCategoryId
        ? this.cmsTicketCategoryService.getTicketGroupsForCategory(
            ticketCategoryId
          )
        : [];
    });

    effect((): void => {
      this.isBeingDragged.set(
        this.ticketDragAndDropSupportService.ticketIsBeingDragged()
      );
    });
  }

  public showSubMenu(): void {
    this.isSubMenuVisible.set(true);
  }
  public hideSubMenu(): void {
    this.isSubMenuVisible.set(false);
  }

  protected handleSubMenuClick(action: ETicketCategorySubMenuAction): void {
    switch (action) {
      case ETicketCategorySubMenuAction.switchToRight:
        this.switchTicketCategoryDirection(ESwitchDirection.Forwards);
        break;
      case ETicketCategorySubMenuAction.switchToLeft:
        this.switchTicketCategoryDirection(ESwitchDirection.Backwards);
        break;
      case ETicketCategorySubMenuAction.rename:
        this.isEditing.set(true);
        break;
      case ETicketCategorySubMenuAction.delete:
        this.deleteTicketCategory();
        break;
    }
    this.hideSubMenu();
  }

  private switchTicketCategoryDirection(direction: ESwitchDirection): void {
    const ticketCategoryId: string | undefined = this.ticketCategory().id;

    if (ticketCategoryId) {
      this.cmsTicketCategoryService.switchTicketCategoryPosition(
        ticketCategoryId,
        direction
      );
    }
  }

  private deleteTicketCategory(): void {
    this.cmsTicketCategoryService.deleteTicketCategory(this.ticketCategory());
  }

  public saveNewName(): void {
    const ticketCategoryId: string | undefined = this.ticketCategory().id;
    const name: string | null | undefined = this.ticketCategoryForm.value.name;

    if (ticketCategoryId && name) {
      this.cmsTicketCategoryService.renameTicketCategory(
        ticketCategoryId,
        name
      );
    }
    this.isEditing.set(false);
  }

  public cancelName(): void {
    this.ticketCategoryForm.controls.name.setValue(this.ticketCategory().name);
    this.isEditing.set(false);
  }

  public openCategoryMultiSelectDialog(): void {
    this.dialogService.openDialog<IDialogDataWithTicketCategory>(
      TicketCategoryMultiSelectDialogComponent,
      {
        headline: this.cmsTextProvider.ticketCategories.assignTickets,
        width: '400px',
        ticketCategory: this.ticketCategory(),
      }
    );
  }

  public removeCategoryId(ticketGroup: ISortGroupWithTicketData): void {
    this.cmsTicketCategoryService.removeTicketFromCategory(ticketGroup);
  }

  public switchTicketsInCategory(
    ticketGroup: ISortGroupWithTicketData,
    direction: ESwitchDirection
  ): void {
    this.cmsTicketCategoryService.moveTicketInCategory(ticketGroup, direction);
  }

  public dropTicket(event: CdkDragDrop<ISortGroupWithTicketData[]>): void {
    const ticketCategoryId: string | undefined = this.ticketCategory().id;
    const ticketGroup: ISortGroupWithTicketData = event.item.data;

    if (!event.isPointerOverContainer || !ticketCategoryId || !ticketGroup) {
      return;
    }

    this.cmsTicketCategoryService.assignTicketToCategory(
      ticketGroup,
      ticketCategoryId,
      event.currentIndex
    );
  }

  public toggleTicketDragSignal(value: boolean): void {
    this.ticketDragAndDropSupportService.ticketIsBeingDragged.set(value);
  }

  public changePlaceholder(viewMode: EDropListViewMode) {
    this.ticketDragAndDropSupportService.currentDropListViewMode.set(viewMode);
  }

  public setHighlightedTicket(isHovered: boolean, index: number): void {
    if (isHovered) {
      this.highlightedTicket.set(index);
    } else {
      this.highlightedTicket.set(undefined);
    }
  }
}
